// components/SelfPaced/FullscreenViewer.js
// ──────────────────────────────────────────────────────────────────────────────
// In-app fullscreen document viewer with:
//   • Portal-based fullscreen overlay (stays inside LMS, no browser fullscreen)
//   • Download / copy / right-click / print protection
//   • AI explanation captions overlay with word-by-word TTS highlighting
//   • Auto-voice on page/slide change (with user toggle)
//   • Light + dark theme support
// ──────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Document, Page } from 'react-pdf';
import { Spinner } from 'react-bootstrap';
import {
  X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut,
  RotateCw, Sparkles, Play, Pause, Volume2, VolumeX,
  Settings, Eye, EyeOff,
} from 'lucide-react';
import { getAIExplanations } from '../../utils/selfPacedApi';
import ContentWatermark from '../Common/ContentWatermark';

// ═════════════════════════════════════════════════════════════════════════════
// Security Layer — blocks downloading, printing, right-click, screenshots
// ═════════════════════════════════════════════════════════════════════════════
function useDocumentSecurity(active) {
  useEffect(() => {
    if (!active) return;

    const blockContext = (e) => e.preventDefault();
    const blockKeys = (e) => {
      // Block Ctrl+S (save), Ctrl+P (print), Ctrl+Shift+I (devtools),
      // Ctrl+U (source), Ctrl+C (copy), PrintScreen
      if (
        (e.ctrlKey && ['s', 'p', 'u', 'c'].includes(e.key.toLowerCase())) ||
        (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'i') ||
        e.key === 'PrintScreen' ||
        e.key === 'F12'
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const blockDragStart = (e) => e.preventDefault();
    const blockCopy = (e) => e.preventDefault();
    const blockPrint = () => {
      // Temporarily hide content during print attempts
      const style = document.createElement('style');
      style.id = 'fs-viewer-print-block';
      style.textContent = '@media print { body { display: none !important; } }';
      document.head.appendChild(style);
    };

    document.addEventListener('contextmenu', blockContext, true);
    document.addEventListener('keydown', blockKeys, true);
    document.addEventListener('dragstart', blockDragStart, true);
    document.addEventListener('copy', blockCopy, true);
    window.addEventListener('beforeprint', blockPrint);

    // Add print-blocking CSS
    blockPrint();

    return () => {
      document.removeEventListener('contextmenu', blockContext, true);
      document.removeEventListener('keydown', blockKeys, true);
      document.removeEventListener('dragstart', blockDragStart, true);
      document.removeEventListener('copy', blockCopy, true);
      window.removeEventListener('beforeprint', blockPrint);
      const el = document.getElementById('fs-viewer-print-block');
      if (el) el.remove();
    };
  }, [active]);
}

// ═════════════════════════════════════════════════════════════════════════════
// TTS Engine with word-by-word highlighting
// ═════════════════════════════════════════════════════════════════════════════
function useTTSWithHighlight() {
  const [ttsState, setTtsState] = useState('idle'); // idle | playing | paused
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const utterRef = useRef(null);
  const wordsRef = useRef([]);

  const speak = useCallback((text) => {
    if (!text || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();

    const words = text.split(/\s+/).filter(Boolean);
    wordsRef.current = words;
    setHighlightIndex(-1);

    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 0.95;
    utter.pitch = 1;

    // Word boundary event for highlighting
    utter.onboundary = (e) => {
      if (e.name === 'word') {
        // Find which word index this boundary corresponds to
        const spoken = text.substring(0, e.charIndex);
        const wordIdx = spoken.split(/\s+/).filter(Boolean).length;
        setHighlightIndex(wordIdx);
      }
    };

    utter.onend = () => {
      setTtsState('idle');
      setHighlightIndex(-1);
    };

    utter.onerror = () => {
      setTtsState('idle');
      setHighlightIndex(-1);
    };

    utterRef.current = utter;
    window.speechSynthesis.speak(utter);
    setTtsState('playing');
  }, []);

  const pause = useCallback(() => {
    window.speechSynthesis.pause();
    setTtsState('paused');
  }, []);

  const resume = useCallback(() => {
    window.speechSynthesis.resume();
    setTtsState('playing');
  }, []);

  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
    setTtsState('idle');
    setHighlightIndex(-1);
  }, []);

  // Cleanup
  useEffect(() => () => {
    window.speechSynthesis.cancel();
  }, []);

  return { ttsState, highlightIndex, words: wordsRef.current, speak, pause, resume, stop };
}

// ═════════════════════════════════════════════════════════════════════════════
// Highlighted Caption Text — renders words with TTS highlight
// ═════════════════════════════════════════════════════════════════════════════
const CaptionText = React.memo(({ text, highlightIndex, isActive }) => {
  const words = useMemo(() => (text || '').split(/\s+/).filter(Boolean), [text]);

  if (!text) return null;

  if (!isActive || highlightIndex < 0) {
    return <span className="fsv-caption__text-plain">{text}</span>;
  }

  return (
    <span className="fsv-caption__text-highlighted">
      {words.map((word, i) => (
        <span
          key={i}
          className={`fsv-caption__word ${i === highlightIndex ? 'fsv-caption__word--active' : ''} ${i < highlightIndex ? 'fsv-caption__word--spoken' : ''}`}
        >
          {word}{' '}
        </span>
      ))}
    </span>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// AI Caption Overlay — shows AI explanation as subtitle-style captions
// ═════════════════════════════════════════════════════════════════════════════
const AICaptionOverlay = ({
  lessonId, pageNumber, autoVoice, onAutoVoiceToggle, tts,
  captionsVisible, onCaptionsToggle,
}) => {
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activated, setActivated] = useState(false);
  const cacheRef = useRef({});
  const totalRef = useRef(0);
  const prevPageRef = useRef(pageNumber);

  // Reset on lesson change
  useEffect(() => {
    cacheRef.current = {};
    totalRef.current = 0;
    setActivated(false);
    setExplanation(null);
    setLoading(false);
    tts.stop();
  }, [lessonId]);

  // Sync explanation on page change
  useEffect(() => {
    if (activated && totalRef.current > 0) {
      const expl = cacheRef.current[pageNumber] ?? null;
      setExplanation(expl);

      // Auto-voice on page change
      if (autoVoice && expl && pageNumber !== prevPageRef.current) {
        setTimeout(() => tts.speak(expl), 300);
      }
    } else {
      tts.stop();
    }
    prevPageRef.current = pageNumber;
  }, [pageNumber, activated, autoVoice]);

  const handleActivate = async () => {
    if (activated) return;
    setActivated(true);
    setLoading(true);
    try {
      const res = await getAIExplanations(lessonId, { page: 1, limit: 200 });
      const data = res.data?.data || res.data;
      const arr = Array.isArray(data) ? data : (data?.explanations || []);
      arr.forEach(e => { cacheRef.current[e.pageNumber] = e.explanation || e.explanationText; });
      totalRef.current = arr.length;
      const expl = cacheRef.current[pageNumber] ?? null;
      setExplanation(expl);
      if (autoVoice && expl) {
        setTimeout(() => tts.speak(expl), 300);
      }
    } catch {
      setExplanation(null);
    } finally {
      setLoading(false);
    }
  };

  // Activate button (if not yet activated)
  if (!activated) {
    return (
      <div className="fsv-caption-bar">
        <button className="fsv-caption-bar__activate" onClick={handleActivate}>
          <Sparkles size={14} /> Enable AI Captions
        </button>
      </div>
    );
  }

  return (
    <div className={`fsv-caption-bar ${captionsVisible ? '' : 'fsv-caption-bar--hidden-captions'}`}>
      {/* Controls row */}
      <div className="fsv-caption-bar__controls">
        <div className="fsv-caption-bar__left">
          <Sparkles size={13} className="fsv-caption-bar__icon" />
          <span className="fsv-caption-bar__label">
            AI Explanation {totalRef.current > 1 ? `— Page ${pageNumber}` : ''}
          </span>
        </div>
        <div className="fsv-caption-bar__right">
          {/* TTS Controls */}
          {'speechSynthesis' in window && explanation && (
            <div className="fsv-caption-bar__tts">
              {tts.ttsState === 'idle' && (
                <button className="fsv-caption-bar__btn" onClick={() => tts.speak(explanation)} title="Listen">
                  <Play size={13} />
                </button>
              )}
              {tts.ttsState === 'playing' && (
                <button className="fsv-caption-bar__btn" onClick={tts.pause} title="Pause">
                  <Pause size={13} />
                </button>
              )}
              {tts.ttsState === 'paused' && (
                <button className="fsv-caption-bar__btn" onClick={tts.resume} title="Resume">
                  <Play size={13} />
                </button>
              )}
              {tts.ttsState !== 'idle' && (
                <button className="fsv-caption-bar__btn" onClick={() => tts.speak(explanation)} title="Replay">
                  <RotateCw size={12} />
                </button>
              )}
            </div>
          )}

          {/* Auto-voice toggle */}
          <button
            className={`fsv-caption-bar__btn fsv-caption-bar__btn--toggle ${autoVoice ? 'fsv-caption-bar__btn--active' : ''}`}
            onClick={onAutoVoiceToggle}
            title={autoVoice ? 'Auto-voice ON' : 'Auto-voice OFF'}
          >
            {autoVoice ? <Volume2 size={13} /> : <VolumeX size={13} />}
            <span className="fsv-caption-bar__btn-label">Auto</span>
          </button>

          {/* Caption visibility toggle */}
          <button
            className={`fsv-caption-bar__btn fsv-caption-bar__btn--toggle ${captionsVisible ? 'fsv-caption-bar__btn--active' : ''}`}
            onClick={onCaptionsToggle}
            title={captionsVisible ? 'Hide captions' : 'Show captions'}
          >
            {captionsVisible ? <Eye size={13} /> : <EyeOff size={13} />}
          </button>
        </div>
      </div>

      {/* Caption text area */}
      {captionsVisible && (
        <div className="fsv-caption-bar__text-area">
          {loading && (
            <div className="fsv-caption-bar__loading">
              <Spinner animation="border" size="sm" variant="light" />
              <span>Loading AI explanation…</span>
            </div>
          )}
          {!loading && !explanation && (
            <div className="fsv-caption-bar__empty">No AI explanation for this page.</div>
          )}
          {!loading && explanation && (
            <div className="fsv-caption-bar__caption">
              <CaptionText
                text={explanation}
                highlightIndex={tts.highlightIndex}
                isActive={tts.ttsState !== 'idle'}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Main Fullscreen Viewer (Portal-based)
// ═════════════════════════════════════════════════════════════════════════════
export default function FullscreenViewer({ url, title, lessonId, fileType, onClose }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [loadError, setLoadError] = useState(false);
  const [captionsVisible, setCaptionsVisible] = useState(true);
  const [autoVoice, setAutoVoice] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const tts = useTTSWithHighlight();
  const [pdfData, setPdfData] = useState(null);
  const [fetching, setFetching] = useState(true);

  // Pre-fetch PDF/PPTX as ArrayBuffer to avoid CORS issues with pdf.js range requests
  useEffect(() => {
    if (!url) { setFetching(false); return; }
    let cancelled = false;
    (async () => {
      try {
        setFetching(true);
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const buf = await resp.arrayBuffer();
        if (!cancelled) setPdfData(new Uint8Array(buf));
      } catch (err) {
        console.error('FullscreenViewer fetch error:', err);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  // Security
  useDocumentSecurity(true);

  // Track container size
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setContainerWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setPageNumber(p => Math.max(1, p - 1));
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        setPageNumber(p => Math.min(numPages || p, p + 1));
      }
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        setScale(s => Math.min(3, s + 0.15));
      }
      if (e.key === '-') {
        e.preventDefault();
        setScale(s => Math.max(0.5, s - 0.15));
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [numPages, onClose]);

  // Stop TTS on close
  useEffect(() => () => tts.stop(), []);

  const onDocLoad = ({ numPages: total }) => {
    setNumPages(total);
    setPageNumber(1);
    setLoadError(false);
  };

  const goPage = (delta) => setPageNumber(p => Math.min(Math.max(1, p + delta), numPages || 1));
  const adjustZoom = (delta) => setScale(s => Math.min(Math.max(0.5, s + delta), 3));

  const pageWidth = Math.min(containerWidth - 80, 1200) * scale;

  // Settings dropdown
  const settingsRef = useRef(null);
  useEffect(() => {
    if (!settingsOpen) return;
    const handler = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [settingsOpen]);

  const content = (
    <div
      className="fsv-overlay"
      ref={containerRef}
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Top toolbar */}
      <div className="fsv-toolbar">
        <div className="fsv-toolbar__left">
          <span className="fsv-toolbar__title">{title || 'Document Viewer'}</span>
          {numPages && (
            <span className="fsv-toolbar__page-info">
              Page {pageNumber} of {numPages}
            </span>
          )}
        </div>

        <div className="fsv-toolbar__center">
          <button className="fsv-toolbar__btn" onClick={() => goPage(-1)} disabled={pageNumber <= 1} title="Previous (←)">
            <ChevronLeft size={18} />
          </button>

          <div className="fsv-toolbar__page-nav">
            <input
              type="number"
              className="fsv-toolbar__page-input"
              value={pageNumber}
              min={1}
              max={numPages || 1}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (val >= 1 && val <= (numPages || 1)) setPageNumber(val);
              }}
            />
            <span className="fsv-toolbar__page-sep">/ {numPages || '—'}</span>
          </div>

          <button className="fsv-toolbar__btn" onClick={() => goPage(1)} disabled={pageNumber >= (numPages || 1)} title="Next (→)">
            <ChevronRight size={18} />
          </button>

          <div className="fsv-toolbar__divider" />

          <button className="fsv-toolbar__btn" onClick={() => adjustZoom(-0.15)} disabled={scale <= 0.5} title="Zoom Out (−)">
            <ZoomOut size={16} />
          </button>
          <span className="fsv-toolbar__zoom-label">{Math.round(scale * 100)}%</span>
          <button className="fsv-toolbar__btn" onClick={() => adjustZoom(0.15)} disabled={scale >= 3} title="Zoom In (+)">
            <ZoomIn size={16} />
          </button>
          <button className="fsv-toolbar__btn" onClick={() => setScale(1)} title="Reset Zoom">
            <RotateCw size={14} />
          </button>
        </div>

        <div className="fsv-toolbar__right">
          {/* Settings dropdown */}
          <div className="fsv-toolbar__settings-wrap" ref={settingsRef}>
            <button
              className="fsv-toolbar__btn"
              onClick={() => setSettingsOpen(o => !o)}
              title="Viewer settings"
            >
              <Settings size={16} />
            </button>
            {settingsOpen && (
              <div className="fsv-settings-dropdown">
                <div className="fsv-settings-dropdown__item">
                  <label className="fsv-settings-dropdown__label">
                    <input
                      type="checkbox"
                      checked={autoVoice}
                      onChange={() => setAutoVoice(v => !v)}
                    />
                    <Volume2 size={13} />
                    Auto-read on page change
                  </label>
                </div>
                <div className="fsv-settings-dropdown__item">
                  <label className="fsv-settings-dropdown__label">
                    <input
                      type="checkbox"
                      checked={captionsVisible}
                      onChange={() => setCaptionsVisible(v => !v)}
                    />
                    <Eye size={13} />
                    Show AI captions
                  </label>
                </div>
              </div>
            )}
          </div>

          <button className="fsv-toolbar__btn fsv-toolbar__btn--close" onClick={onClose} title="Exit fullscreen (Esc)">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Document area */}
      <div className="fsv-body" ref={scrollRef}>
        {loadError ? (
          <div className="fsv-error">
            <p>Unable to load this document.</p>
            <button className="fsv-error__retry" onClick={() => { setLoadError(false); setPageNumber(1); }}>
              <RotateCw size={14} /> Retry
            </button>
          </div>
        ) : fetching ? (
          <div className="fsv-loading"><Spinner animation="border" variant="light" /></div>
        ) : pdfData ? (
          <div className="fsv-document" style={{ pointerEvents: 'auto' }}>
            <Document
              file={{ data: pdfData ? pdfData.slice() : null }}
              onLoadSuccess={onDocLoad}
              onLoadError={() => setLoadError(true)}
              loading={<div className="fsv-loading"><Spinner animation="border" variant="light" /></div>}
            >
              <Page
                pageNumber={pageNumber}
                width={pageWidth}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </Document>
          </div>
        ) : null}}
      </div>

      {/* AI Caption overlay at the bottom */}
      {lessonId && (
        <AICaptionOverlay
          lessonId={lessonId}
          pageNumber={pageNumber}
          autoVoice={autoVoice}
          onAutoVoiceToggle={() => setAutoVoice(v => !v)}
          tts={tts}
          captionsVisible={captionsVisible}
          onCaptionsToggle={() => setCaptionsVisible(v => !v)}
        />
      )}

      {/* Watermark overlay with student & institute name for content protection */}
      <ContentWatermark />
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}
